import type { Correlations } from "@/composables/forms/formTypes";
import type {
  BasicData,
  FluidPropertiesOverrides,
  FlowRatePrerequisites,
  FlowRatesAtSurface,
  FlowRatesInSitu,
  FluidPropertiesContext
} from "./d";
import { getFlowRatePrerequisites } from "./getFlowRatePrerequisites";
import { getFlowRatesInSitu } from "./getFlowRatesInSitu";
import { getFlowRateDependants } from "./getFlowRateDependants";
import { getFluidFrictionProperties } from "./fluidFrictionProperties/getFluidFrictionProperties";
import { Tubing_Flow_Options_Enum } from "gql/api";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import { getGasProperties } from "../gasPhase/gasTable";
import { API, PoundsPerCubicFoot } from "@/libs/units/density";
import { withoutAbsentGasComponents } from "@/libs/calculations/gasPhase/gasTable";

export function getFluidPropertiesContext(
  basicData: BasicData,
  correlations: Correlations,
  pressurePSIA: number,
  temperatureF: number,
  pipeDiameterIn: number,
  flowRatesAtSurface: FlowRatesAtSurface,
  flowRatesInSituIn?: FlowRatesInSitu,
  flowRatePrerequisitesIn?: FlowRatePrerequisites,
  customFluidProperties?: LagrangeInterpolationMapWithGroupings | null,
  customViscosityProperties?: LagrangeInterpolationMapWithGroupings | null,
  bubblePointPressurePSIAOverride = 0,
  dewPointPressurePSIAOverride = 0,
  equipmentCrossSectionFt2 = 0,
  gasSeparationEfficiencyRatio = 0,
  overrides: FluidPropertiesOverrides = {},
  condensateApiOverride: number | null = null,
  lockCondensateRate: boolean = false
): FluidPropertiesContext {
  const gasOilRatioSCFSTB =
    flowRatesInSituIn?.gasOilRatioSCFSTB ||
    flowRatesAtSurface?.gasOilRatioSCFSTB ||
    0;

  const gasLiquidRatioSCFSTB =
    flowRatesInSituIn?.gasLiquidRatioSCFSTB ||
    flowRatesAtSurface?.gasLiquidRatioSCFSTB ||
    0;

  const waterCutRatio =
    flowRatesInSituIn?.waterCutRatio || flowRatesAtSurface?.waterCutRatio || 0;

  // This contains gas properties and peng robinson gas compressibility factors
  // Also has peng robinson oil density and condensate density
  const gasProperties = getGasProperties(
    basicData,
    temperatureF,
    pressurePSIA,
    flowRatesAtSurface?.gasFlowRateSCFD || 0
  );

  // PVT!AW25
  let condensateSpecificGravity = 0;
  let condensateApi = 0;
  if (condensateApiOverride !== null && condensateApiOverride > 0) {
    condensateSpecificGravity = API.toStandardUnit(condensateApiOverride);
    condensateApi = condensateApiOverride;
  } else {
    condensateSpecificGravity = PoundsPerCubicFoot.toStandardUnit(
      withoutAbsentGasComponents(gasProperties).reduce(
        (sum, g) => sum + g.condensateSTp,
        0
      )
    );
    if (condensateSpecificGravity > 0) {
      condensateApi = API.toThisUnit(condensateSpecificGravity);
    }
  }

  // flowRatePrerequisites
  const flowRatePrerequisites: FlowRatePrerequisites = getFlowRatePrerequisites(
    basicData,
    gasOilRatioSCFSTB,
    gasLiquidRatioSCFSTB,
    waterCutRatio,
    correlations,
    pressurePSIA,
    temperatureF,
    gasProperties,
    bubblePointPressurePSIAOverride,
    dewPointPressurePSIAOverride,
    customFluidProperties ?? null,
    customViscosityProperties ?? null,
    condensateApi,
    condensateSpecificGravity
  );

  const flowRatesInSitu = getFlowRatesInSitu(
    flowRatePrerequisites,
    gasSeparationEfficiencyRatio,
    basicData,
    flowRatesAtSurface,
    flowRatesInSituIn,
    flowRatePrerequisitesIn,
    gasProperties,
    lockCondensateRate
  );

  // flowRateDependents
  const flowRateDependents = getFlowRateDependants(
    basicData,
    flowRatePrerequisites,
    flowRatesInSitu,
    pressurePSIA,
    temperatureF,
    pipeDiameterIn,
    equipmentCrossSectionFt2,
    overrides.fluidViscosityCPOverride
  );

  const fluidProperties = {
    ...flowRatePrerequisites,
    ...flowRateDependents,
    ...flowRatesInSitu
  };

  // For inside of tubing
  const frictionCorrelation =
    correlations?.tubing_flow || Tubing_Flow_Options_Enum.BeggsBrill;

  // For inside of tubing
  const fluidFrictionProperties = getFluidFrictionProperties(
    basicData,
    frictionCorrelation,
    fluidProperties,
    pipeDiameterIn,
    pressurePSIA // this is called pumpIntakePressurePSIA downstack, but since we are doing this in multiple places...
  );

  return {
    gasProperties,
    flowRatesAtSurface,
    flowRatesInSitu,
    flowRatePrerequisites,
    flowRateDependents,
    fluidProperties,
    fluidFrictionProperties
  };
}
